import React, { useState, useEffect, useCallback } from "react"; // Added useCallback
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index.js";
import { IoMdArrowBack } from "react-icons/io";
import { 
  FiEdit, FiPlus, FiLogOut, FiHome, FiPlusSquare, FiCreditCard,
  FiMail, FiUser, FiMapPin, FiInfo, FiTag, FiImage, FiSettings 
} from "react-icons/fi";
import { GiWallet } from "react-icons/gi";

const SellerProfile = ({ refreshAuth }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    profileImage: "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg",
    phone: "",
    lga: "",
    address: "",
    bio: "",
    fields: [],
    shopImages: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const userId = decoded?.id || decoded?._id;
  const typeofuser = decoded?.typeofuser;

  // 1. Wrapped fetch logic in useCallback to match mobile performance/logic
  const fetchProfile = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await api.UserProfile(userId);
      setProfile({
        name: response.data?.Name || "N/A",
        email: response.data?.Email || "N/A",
        profileImage: response.data?.profileImage
          ? `${response.data.profileImage}`
          : "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg",
        phone: response.data?.phone || "N/A",
        lga: response.data?.lga || "N/A",
        address: response.data?.address || "N/A",
        bio: response.data?.bio || "N/A",
        fields: response.data?.fields || [],
        shopImages: response.data?.shopImages || [],
      });
    } catch (err) {
      setError("⚠️ Failed to fetch profile.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // 2. Added auto-refresh on window focus (Web alternative to useFocusEffect)
  useEffect(() => {
    fetchProfile();
    window.addEventListener("focus", fetchProfile);
    return () => window.removeEventListener("focus", fetchProfile);
  }, [fetchProfile]);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      localStorage.removeItem("token");
      if (refreshAuth) refreshAuth();
      navigate("/auth");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-24">
      {/* 🔝 Sticky Header with Settings Icon */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition flex items-center gap-1 text-gray-700"
        >
          <IoMdArrowBack size={24} />
          <span className="hidden sm:inline font-bold">Back</span>
        </button>
        
        <h1 className="text-lg font-black text-gray-900 tracking-tight">
          {typeofuser === "Seller" ? "Seller Profile" : "Profile"}
        </h1>

        {/* ⚙️ New Settings Icon */}
        <button 
          onClick={() => navigate("/settings")}
          className="p-2 text-gray-700 hover:bg-gray-100 rounded-full transition"
        >
          <FiSettings size={22} />
        </button>
      </div>

      <div className="max-w-3xl mx-auto w-full p-4 sm:p-6">
        
        {/* 👤 Profile Identity Section */}
        <div className="flex flex-col items-center mb-10">
          <div className="relative group">
            <div className="w-36 h-36 rounded-[2.5rem] bg-white p-1.5 shadow-xl border border-gray-100 overflow-hidden transition-transform duration-500">
              <img
                src={profile.profileImage}
                alt="Profile"
                className="object-cover w-full h-full rounded-[2.1rem]"
              />
            </div>
            <button 
                onClick={() => navigate("/profile-seller-edit")}
                className="absolute -bottom-2 -right-2 bg-orange-600 text-white p-2.5 rounded-2xl shadow-lg border-4 border-gray-50 hover:bg-orange-700 transition-all active:scale-95"
            >
              <FiEdit size={18} />
            </button>
          </div>
          <h2 className="mt-4 text-3xl font-black text-gray-900">{profile.name}</h2>
          <p className="text-orange-600 font-bold text-xs uppercase tracking-widest bg-orange-50 px-4 py-1 rounded-full mt-2">
            Verified {typeofuser}
          </p>
        </div>

        {/* 📋 Details Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest ml-2 flex items-center gap-2">
            <FiInfo /> Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: "Name", value: profile.name },
              { label: "Email", value: profile.email },
              { label: "Phone Number", value: profile.phone },
              { label: "LGA", value: profile.lga },
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block">
                  {item.label}
                </label>
                <p className="text-gray-900 font-bold">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block flex items-center gap-1">
              <FiMapPin size={10}/> Address
            </label>
            <p className="text-gray-900 font-bold">{profile.address}</p>
          </div>

          <div className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block">
              Bio
            </label>
            <p className="text-gray-700 font-medium leading-relaxed">{profile.bio}</p>
          </div>

          {/* 🏷 Categories Section */}
          {typeofuser === "Seller" && (
            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm mt-6">
              <label className="text-sm font-black text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-tight">
                <FiTag className="text-orange-600" /> Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {profile.fields && profile.fields.length > 0 ? (
                  profile.fields.map((field, idx) => {
                    const text =
                      typeof field === "object"
                        ? `${field.category || ""} → ${field.sub || ""} → ${field.item || ""}`
                        : field;

                    return (
                      <span
                        key={idx}
                        className="bg-orange-50 text-orange-700 px-4 py-2 rounded-2xl text-xs font-bold border border-orange-100 shadow-sm"
                      >
                        {text}
                      </span>
                    );
                  })
                ) : (
                  <p className="text-gray-400 italic text-sm">No categories selected</p>
                )}
              </div>
            </div>
          )}

          {/* 🖼 Gallery Section */}
          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm mt-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-black text-gray-900 flex items-center gap-2 uppercase tracking-tight">
                <FiImage className="text-orange-600" /> Shop Gallery
              </label>
              <button
                onClick={() => navigate("/profile-seller-edit")}
                className="px-4 py-1.5 bg-orange-600 text-white rounded-xl text-xs font-bold hover:bg-orange-700 transition shadow-lg shadow-orange-100 flex items-center gap-1"
              >
                <FiPlus /> Add Image
              </button>
            </div>

            {profile.shopImages.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {profile.shopImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="aspect-square rounded-2xl overflow-hidden shadow-md group border-2 border-white"
                  >
                    <img
                      src={`${img}`}
                      alt="Shop"
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center border-2 border-dashed border-gray-100 rounded-3xl">
                <p className="text-gray-400 text-sm font-medium">No gallery images uploaded yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* 🛠 Footer Actions */}
        <div className="mt-10 flex flex-col gap-3">
          <button
            onClick={() => navigate("/profile-seller-edit")}
            className="w-full py-4 bg-gray-900 text-white font-bold rounded-[1.5rem] hover:bg-orange-600 transition-all shadow-lg shadow-gray-200 active:scale-95"
          >
            Edit Profile
          </button>
          <button
            onClick={handleLogout}
            className="w-full py-4 bg-white text-red-600 border border-red-100 font-bold rounded-[1.5rem] hover:bg-red-50 transition-all flex items-center justify-center gap-2"
          >
            <FiLogOut /> Sign Out
          </button>
        </div>

        {error && (
          <p className="mt-6 text-center text-sm font-black text-red-500 bg-red-50 p-4 rounded-3xl border border-red-100 italic">
            {error}
          </p>
        )}
      </div>

      {/* ⭐ Bottom Navbar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-gray-100 md:flex justify-around items-center py-3 z-[100] hidden shadow-[0_-5px_15px_rgba(0,0,0,0.05)]">
        <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
          <FiHome size={22} />
          <span className="text-[10px] font-bold">Home</span>
        </button>
        <button onClick={() => navigate("/sellproduct")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
          <FiPlusSquare size={22} />
          <span className="text-[10px] font-bold">Post</span>
        </button>
         <button onClick={() => navigate(`/balancepage/${userId}`)} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                                            <FiCreditCard size={22} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">Wallet</span>
                                          </button>
        <button onClick={() => navigate("/conversations")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
          <FiMail size={22} />
          <span className="text-[10px] font-bold">Inbox</span>
        </button>
        <button 
          onClick={() => (typeofuser === "Customer" || typeofuser === "Rider") ? navigate("/profile") : navigate("/profile-seller")} 
          className="flex flex-col items-center gap-1 text-orange-600 transition-colors"
        >
          <FiUser size={22} />
          <span className="text-[10px] font-bold">Profile</span>
        </button>
      </nav>
    </div>
  );
};

export default SellerProfile;